import { MUIMarkdown } from '@modules/common/components/MUIMarkdown';
import { MarkdownTypewriter } from '@modules/common/components/MUIMarkdown/MarkdownTypewriter';
import { ScrollToBottomButton } from '@modules/common/components/ScrollToBottomButton';
import { FeedbackListSummaryPrediction } from '@modules/oracle/components/FeedbackListSummary/FeedbackListSummaryPrediction';
import { FeedbackProofIconButton } from '@modules/oracle/components/FeedbackProofIconButton';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { CustomMarkdownComponents } from '@modules/oracle/types';
import {
  constructMarkdownFromFeedbackSummaryResponse,
  getMarkdownForFeedbackSummaryMessage,
} from '@modules/oracle/utils';
import { Box, BoxProps, SvgIconProps, TypographyProps } from '@mui/material';
import { Label } from 'generated/graphql';
import { throttle } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';

const trackFeedbackListSummaryEvents = createAnalyticsTracker(
  'feedbackListSummary',
);

const SCROLL_DELTA_OFFSET = 16;

export interface FeedbackListSummaryProps {
  predictionProps?: {
    iconColor?: SvgIconProps['color'];
    textProps?: TypographyProps;
    onMouseEnter?: (
      event: React.MouseEvent,
      prediction: Label | undefined,
      feedbackCount: number | null,
    ) => void;
    onMouseLeave?: () => void;
  };
  containerProps?: BoxProps;
  openCitedArtefactsInNewTab?: boolean;
  isFeedbackSidebarView?: boolean;
  shouldSkipTypingAnimation?: boolean;
}

export function FeedbackListSummary({
  predictionProps,
  containerProps = {},
  openCitedArtefactsInNewTab = false,
  isFeedbackSidebarView,
  shouldSkipTypingAnimation = false,
}: FeedbackListSummaryProps) {
  const messages = useFeedbackSummaryStore(({ data }) => data.messages);
  const isCompleted = useFeedbackSummaryStore(
    ({ visual }) => visual.isCompleted,
  );
  const isFirstTokenStreamedTracked = useRef(false);
  const isLastTokenStreamTracked = useRef(false);

  const summaryContainerRef = useRef<HTMLDivElement>(null);

  const [shouldShowScrollToBottomButton, setShouldShowScrollToBottomButton] =
    useState(false);

  const feedbackSummaryListMarkdown = useMemo(
    () =>
      constructMarkdownFromFeedbackSummaryResponse(
        messages,
        getMarkdownForFeedbackSummaryMessage,
      ),
    [messages],
  );

  useEffect(() => {
    if (!messages.length || isFirstTokenStreamedTracked.current) {
      return;
    }

    trackFeedbackListSummaryEvents({
      eventName: 'Feedback List Summary - First token streamed',
    });

    isFirstTokenStreamedTracked.current = true;
  }, [messages.length]);

  useEffect(() => {
    if (!isCompleted || isLastTokenStreamTracked.current) {
      return;
    }

    trackFeedbackListSummaryEvents({
      eventName: 'Feedback List Summary - Last token streamed',
    });

    isLastTokenStreamTracked.current = true;
  }, [isCompleted]);

  const isScrollAtBottom = () => {
    const container = summaryContainerRef.current;

    if (!container) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;

    return scrollTop + clientHeight + SCROLL_DELTA_OFFSET >= scrollHeight;
  };

  const handleScroll = useMemo(
    () =>
      throttle(() => {
        setShouldShowScrollToBottomButton(!isScrollAtBottom());
      }, 250),
    [],
  );

  useEffect(() => {
    handleScroll();
  }, [summaryContainerRef.current?.scrollHeight, handleScroll]);

  const scrollToBottomCTA = shouldShowScrollToBottomButton ? (
    <ScrollToBottomButton
      onClick={() => {
        setShouldShowScrollToBottomButton(false);
      }}
      containerRef={summaryContainerRef}
    />
  ) : null;

  const components: CustomMarkdownComponents = useMemo(() => {
    return {
      reason: (props) => {
        return (
          <FeedbackListSummaryPrediction {...props} {...predictionProps} />
        );
      },
      tk: (props) => {
        return (
          <FeedbackListSummaryPrediction {...props} {...predictionProps} />
        );
      },
      'feedback-count': (props) => {
        return (
          <FeedbackProofIconButton
            {...props}
            openCitedArtefactsInNewTab={openCitedArtefactsInNewTab}
            isFeedbackSidebarView={isFeedbackSidebarView}
          />
        );
      },
    };
  }, [predictionProps, openCitedArtefactsInNewTab, isFeedbackSidebarView]);

  const onCopy = () => {
    const selectedText = window.getSelection()?.toString();
    trackFeedbackListSummaryEvents({
      eventName: 'Feedback List Summary - Manual Copy Summary of Summaries',
      copiedSummaryText: selectedText,
    });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
      }}
    >
      {scrollToBottomCTA}
      <Box
        {...containerProps}
        sx={containerProps?.sx}
        onCopy={onCopy}
        ref={summaryContainerRef}
        onWheel={() => {
          handleScroll();
        }}
        onTouchStart={() => {
          handleScroll();
        }}
      >
        {shouldSkipTypingAnimation ? (
          <MUIMarkdown
            markdown={feedbackSummaryListMarkdown}
            components={components}
          />
        ) : (
          <MarkdownTypewriter
            markdown={feedbackSummaryListMarkdown}
            components={components}
            isLoading={!isCompleted}
          />
        )}
      </Box>
      <Box
        sx={{
          position: 'sticky',
          top: '100%',
          background: (theme) =>
            shouldShowScrollToBottomButton
              ? `linear-gradient(to bottom, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper} 100%)`
              : undefined,
          overflowX: 'clip',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            margin: '0 auto',
            boxShadow: shouldShowScrollToBottomButton
              ? (theme) =>
                  `${theme.palette.background.paper} -20px -3px 20px 28px}`
              : undefined,
          },
        }}
      ></Box>
    </Box>
  );
}
